// src/data/operationsClient.js
//
// Real client for the cross-cutting /api/bulk-import endpoint plus the
// authenticated-user lookup used for UI role gating.
//
// Scope kept narrow on purpose:
//   - bulkImport(snapshot)         : strict insert, 409 on collision
//   - getCurrentUser()             : returns the authenticated user
//   - _resetCurrentUserCacheForTests() : test seam
//
// Everything else under /api/operations* (audit POST, FX rates, sync
// events) is still served by ad-hoc calls scattered across services/
// — we don't fold them in here until each gets a real UI.
//
// History: this module previously exposed probeCurrentRole(), which
// inferred the role from the status code returned by POST /api/bulk-import.
// That probe could only distinguish admin (400) from "not admin" (403),
// collapsing editor and viewer into the same bucket and breaking
// editor-gated UI. TS4 replaces it with a real GET /api/me lookup.

import {
  ConflictError,
  NetworkError,
  ValidationError,
  errorFromResponse,
} from "./errors.js";
import { invalidatePortfolioCaches as invalidateAll } from "../services/cacheBus.js";
import { recordAudit } from "../services/auditLog.js";
import { log } from "../utils/logger.js";
export { buildFeedbackPayload, submitFeedback } from "./feedbackClient.js";

// ─── Internal: fetch wrapper that throws typed errors ─────────────────────

async function jsonRequest(url, init) {
  let res;
  try {
    res = await fetch(url, {
      ...init,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    });
  } catch (err) {
    log.error("operationsClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("operationsClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  if (res.status === 204) return null;
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

// ─── bulkImport ───────────────────────────────────────────────────────────

/**
 * Strict-insert bulk import of normalized locations. The server runs the
 * whole payload in one transaction; any duplicate primary key collapses
 * the import with a 409 (ConflictError) and rolls back. Re-running after
 * a 409 requires the admin to dedupe locally and retry — there is no
 * upsert path on purpose, so silent drift can't be papered over.
 *
 * @param {Array<object>} snapshot   Output of normalizeForSchema (snake_case).
 * @returns {Promise<{ migrated: number, locationIds: string[], inserted: object }>}
 * @throws  {NetworkError | AuthError | ValidationError | ConflictError}
 * Audit:  location.bulk_import (one row per imported id, source='migration'
 *         on the server side; the client also queues a thin envelope per
 *         id so the client-side audit timeline includes the migration).
 */
export async function bulkImport(snapshot) {
  if (!Array.isArray(snapshot)) {
    throw new ValidationError("bulkImport: snapshot must be an array of normalized locations");
  }
  if (snapshot.length === 0) {
    throw new ValidationError("bulkImport: snapshot is empty; nothing to import");
  }

  const result = await jsonRequest("/api/bulk-import", {
    method: "POST",
    body: JSON.stringify(snapshot),
  });

  // Bust portfolio caches so the next read sees the freshly imported rows.
  invalidateAll();

  // The server already writes per-location audit rows (source='migration').
  // We also queue a client-side envelope per id so the existing audit UI,
  // which reads from the localStorage queue, shows the operation alongside
  // hand-edits without needing a separate "migrations" surface.
  for (const id of result.locationIds || []) {
    recordAudit({
      action: "location.bulk_import",
      entityType: "location",
      entityId: id,
      before: null,
      after: null,
    });
  }
  log.api("operationsClient.bulkImport", {
    migrated: result.migrated,
    inserted: result.inserted,
  });
  return result;
}

// ─── getCurrentUser ───────────────────────────────────────────────────────
//
// GET /api/me. Returns the authenticated user record:
//   { id, okta_sub, email, display_name, role, custom_photo_url, okta_photo_url }
//
// Cached for the page lifetime — the role only changes on
// reauthentication, which is a fresh load anyway. Page-lifetime
// in-memory only; never written to localStorage.

let cachedUser = null;
let inFlight   = null;

/**
 * @typedef {{
 *   id:            string,
 *   custom_photo_url?: string|null,
 *   okta_photo_url?: string|null,
 *   okta_sub:      string,
 *   email:         string,
 *   display_name:  string,
 *   role:          "admin" | "editor" | "viewer" | "view_limited",
 *   defaultView:   "home" | "portfolio",
 *   sensitivityPolicy: object,
 * }} CurrentUser
 *
 * @returns {Promise<CurrentUser>}
 * @throws  {NetworkError | AuthError}
 */
export async function getCurrentUser() {
  if (cachedUser) return cachedUser;
  if (inFlight)   return inFlight;

  inFlight = (async () => {
    try {
      const user = await jsonRequest("/api/me", { method: "GET" });
      // A photo write may finish while this read is in flight; the write is
      // newer than the read, so its fields win over the stale response.
      cachedUser = photoPatch && user ? { ...user, ...photoPatch } : user;
      return cachedUser;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

// Test seam: reset the in-module cache between tests so role flips are
// observable without reloading the entire module graph.
export function _resetCurrentUserCacheForTests() {
  cachedUser = null;
  inFlight = null;
  photoPatch = null;
  userListeners.clear();
}

// ─── Profile photo ────────────────────────────────────────────────────────
//
// POST   /api/me/photo (multipart "photo") -> { custom_photo_url, okta_photo_url }
// DELETE /api/me/photo                      -> { custom_photo_url, okta_photo_url }
//
// Only the two photo fields are returned. They are merged into the cached
// identity (role, devActAs, sensitivityPolicy etc. preserved) and broadcast
// to subscribers so AppShell can patch its identity state immediately.

let photoPatch = null;
const userListeners = new Set();

/** Subscribe to identity patches. Returns an unsubscribe function. */
export function subscribeCurrentUser(listener) {
  userListeners.add(listener);
  return () => userListeners.delete(listener);
}

function pickPhotoFields(body) {
  for (const field of ["custom_photo_url", "okta_photo_url"]) {
    if (!body || !Object.hasOwn(body, field) || (body[field] !== null && typeof body[field] !== "string")) {
      throw new NetworkError("Invalid profile photo response. Please retry.");
    }
  }
  return {
    custom_photo_url: body.custom_photo_url,
    okta_photo_url: body.okta_photo_url,
  };
}

function applyPhotoPatch(patch) {
  photoPatch = patch;
  if (cachedUser) cachedUser = { ...cachedUser, ...patch };
  for (const listener of [...userListeners]) {
    try { listener(patch); } catch (err) { log.error("operationsClient: listener failed", { err: String(err) }); }
  }
  return patch;
}

async function photoRequest(init) {
  let res;
  try {
    res = await fetch("/api/me/photo", { ...init, credentials: "include" });
  } catch (err) {
    throw new NetworkError("Failed to reach /api/me/photo", err);
  }
  if (!res.ok) throw await errorFromResponse(res);
  let body;
  try { body = await res.json(); } catch (err) {
    throw new NetworkError("Malformed JSON from /api/me/photo", err);
  }
  return applyPhotoPatch(pickPhotoFields(body));
}

/**
 * @param {Blob} blob  Native canvas-exported 256px square PNG image.
 * @returns {Promise<{custom_photo_url: string|null, okta_photo_url: string|null}>}
 */
export async function uploadProfilePhoto(blob) {
  if (typeof Blob === "undefined" || !(blob instanceof Blob)) {
    throw new ValidationError("uploadProfilePhoto: a photo blob is required");
  }
  const form = new FormData();
  form.append("photo", blob, blob.type === "image/jpeg" ? "photo.jpg" : "photo.png");
  // No Content-Type header: the browser sets the multipart boundary.
  return photoRequest({ method: "POST", body: form });
}

/** Removes the custom photo; identity falls back to Okta photo or initials. */
export async function removeProfilePhoto() {
  return photoRequest({ method: "DELETE" });
}

// Re-export the error classes for callers that import the client.
export { ConflictError, NetworkError, ValidationError };
